import { FullWidthContentWrapper } from 'commons/components/workflow-overlay/workflow-overlay.style';
import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import { ModuleScrollableContent } from 'layout';
import { ConfigurationListProvider } from 'modules/settings/contexts/device-configuration/providers/configuration-list.provider';
import { useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { SettingsDevicesConfigStatusTrackingContainer } from '../settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.container';
import { SettingsDevicesConfigurationsBulkActionsContainer } from '../settings-devices-configuration-bulk-actions/settings-devices-configuration-bulk-actions.container';
import { SettingsDevicesConfigurationList } from '../settings-devices-configuration-list/settings-devices-configuration-list.component';
import { SettingsDevicesCustomAlarmListContainer } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/settings-devices-custom-alarm-list.container';
import { SettingsDevicesConfigurationDashboardHeader } from './components/header/settings-devices-configuration-dashboard-header.component';
import {
    DEVICE_CONFIGURATION_TABS,
    DeviceConfigurationTabs,
    isDeviceConfigurationTab,
} from './settings-device-configuration-dashboard.constants';
import { ModuleContentSettings, SettingsTableSeparator } from './settings-device-configuration-dashboard.style';
import { CustomAlarmBulkActionsContainer } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/components/custom-alarm-bulk-actions/custom-alarm-bulk-actions.container';
import { CustomAlarmsListProvider } from '../settings-devices-custom-alarm-list/providers/custom-alarms-list.provider';

export const SettingsDevicesConfigurationDashboardContainer: React.FC = () => {
    const { id } = useParams();
    const { state } = useLocation();
    const selectedTabFromState = state?.tab;
    const [selectedTab, setSelectedTab] = useState<DeviceConfigurationTabs>(
        isDeviceConfigurationTab(selectedTabFromState) ? selectedTabFromState : DEVICE_CONFIGURATION_TABS.CONFIGURATIONS
    );

    // Reset to the default tab when the selected device changes. Switching device from the side
    // menu keeps this container mounted (same route, different :id), so selectedTab would otherwise
    // survive across devices. Adjusting during render (vs. useEffect) avoids a flash of the old tab
    // and does not override a deep-linked state.tab on mount (custom-alarm flow remounts the dashboard).
    const [previousDeviceId, setPreviousDeviceId] = useState(id);
    if (id !== previousDeviceId) {
        setPreviousDeviceId(id);
        setSelectedTab(DEVICE_CONFIGURATION_TABS.CONFIGURATIONS);
    }

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { getDeviceTypeDetailsBySlug } = useDeviceTypes();
    const deviceType = getDeviceTypeDetailsBySlug(id ?? '');

    return (
        <CustomAlarmsListProvider enabled={selectedTab === 'CUSTOM_ALARMS'}>
            <ConfigurationListProvider enabled={selectedTab === 'CONFIGURATIONS'} deviceType={deviceType}>
                <FullWidthContentWrapper>
                    <ModuleScrollableContent ref={scrollContainerRef}>
                        <SettingsDevicesConfigurationDashboardHeader
                            title={deviceType?.name ?? ''}
                            selectedTab={selectedTab}
                            onTabChange={setSelectedTab}
                        />
                        <SettingsTableSeparator />
                        <ModuleContentSettings>
                            {selectedTab === 'CONFIGURATIONS' && (
                                <SettingsDevicesConfigurationList scrollContainerRef={scrollContainerRef} />
                            )}
                            {selectedTab === 'STATUS_TRACK' && <SettingsDevicesConfigStatusTrackingContainer />}
                            {selectedTab === 'CUSTOM_ALARMS' && <SettingsDevicesCustomAlarmListContainer />}
                        </ModuleContentSettings>
                    </ModuleScrollableContent>
                    <CustomAlarmBulkActionsContainer />
                    <SettingsDevicesConfigurationsBulkActionsContainer />
                </FullWidthContentWrapper>
            </ConfigurationListProvider>
        </CustomAlarmsListProvider>
    );
};
